import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/networks/networksApi'
import {
  searchParams,
  statusParams,
  type ObjectStatus,
  type UsageParam,
} from '@/services/core/listSearch'
import { fetchAllPages, sortParams, type ListSort } from '@/services/core/paging'
import { mapValidationWarnings, type ValidationWarning } from '@/services/core/validationWarnings'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

export interface SiteRulesetRef {
  id: string
  name: string
}

export interface Site {
  id: string
  name: string
  objectState: string
  labels: string[]
  country: string
  politicalAdvertising: string
  paywalled: boolean
  iabCategories: string[]
  earningsAccountId: string
  earningsAccountName: string
  filterTeamsType: string
  filterTeams: string[]
  requestedInLastDay: boolean
  validationWarnings: ValidationWarning[]
  /** The site groups this site belongs to — membership is held on the site. */
  siteGroups: SiteRulesetRef[]
  siteRuleset: SiteRulesetRef | null
  targetingRulesets: SiteRulesetRef[]
  sizeRulesets: SiteRulesetRef[]
  layoutRulesets: SiteRulesetRef[]
  ownerTeamId: string
  ownerTeamName: string
  createTime: string
  updateTime: string
}

/** One row of the network's site-country summary. */
export interface SiteCountry {
  country: string
  count: number
}

export interface SiteInput {
  name: string
  country?: string
  politicalAdvertising?: string
  paywalled?: boolean
  iabCategories?: string[]
  earningsAccountId?: string
  ownerTeamId?: string
  labels?: string[]
  objectState?: string
  /** Site group ids — the API takes plain ids here, not references. */
  siteGroups?: string[]
  filterTeamsType?: string
  filterTeams?: { id: string }[]
  siteRuleset?: { id: string } | null
  targetingRulesets?: { id: string }[]
  sizeRulesets?: { id: string }[]
  layoutRulesets?: { id: string }[]
}

function mapRulesetRef(r: Record<string, unknown>): SiteRulesetRef {
  return { id: (r.id as string) ?? '', name: (r.name as string) ?? '' }
}

function mapSite(r: Record<string, unknown>): Site {
  const ea = r.earningsAccount as Record<string, unknown> | undefined
  const sr = r.siteRuleset as Record<string, unknown> | undefined
  // GET responses use `ownerTeam`; POST responses use `team`
  const ownerTeam = (r.ownerTeam ?? r.team) as Record<string, unknown> | undefined
  return {
    id: (r.id as string) ?? '',
    name: (r.name as string) ?? '',
    objectState: (r.objectState as string) ?? '',
    labels: (r.labels as string[]) ?? [],
    country: (r.country as string) ?? '',
    politicalAdvertising: (r.politicalAdvertising as string) ?? '',
    paywalled: (r.paywalled as boolean) ?? false,
    iabCategories: (r.iabCategories as string[]) ?? [],
    earningsAccountId: (ea?.id as string) ?? '',
    earningsAccountName: (ea?.name as string) ?? '',
    ownerTeamId: (ownerTeam?.id as string) ?? '',
    ownerTeamName: (ownerTeam?.name as string) ?? '',
    filterTeamsType: (r.filterTeamsType as string) ?? '',
    filterTeams: ((r.filterTeams as Array<Record<string, unknown>>) ?? []).map(t => (t.id as string) ?? ''),
    requestedInLastDay: (r.requestedInLastDay as boolean) ?? false,
    validationWarnings: mapValidationWarnings(r.validationWarnings),
    siteGroups: ((r.siteGroups as Record<string, unknown>[]) ?? []).map(mapRulesetRef),
    siteRuleset: sr ? mapRulesetRef(sr) : null,
    targetingRulesets: ((r.targetingRulesets as Record<string, unknown>[]) ?? []).map(mapRulesetRef),
    sizeRulesets: ((r.sizeRulesets as Record<string, unknown>[]) ?? []).map(mapRulesetRef),
    layoutRulesets: ((r.layoutRulesets as Record<string, unknown>[]) ?? []).map(mapRulesetRef),
    createTime: (r.createTime as string) ?? '',
    updateTime: (r.updateTime as string) ?? '',
  }
}

function toApiBody(input: SiteInput): Record<string, unknown> {
  const { earningsAccountId, ownerTeamId, filterTeams, siteRuleset, targetingRulesets, sizeRulesets, layoutRulesets, ...rest } = input
  const body: Record<string, unknown> = { ...rest }
  if (earningsAccountId) body.earningsAccount = { id: earningsAccountId }
  if (ownerTeamId) body.team = { id: ownerTeamId }
  if (filterTeams !== undefined) body.filterTeams = filterTeams
  if (siteRuleset !== undefined) body.siteRuleset = siteRuleset
  if (targetingRulesets !== undefined) body.targetingRulesets = targetingRulesets
  if (sizeRulesets !== undefined) body.sizeRulesets = sizeRulesets
  if (layoutRulesets !== undefined) body.layoutRulesets = layoutRulesets
  return body
}

/**
 * The list endpoint caps a page at 100 rows. A screen shows one page and stops
 * there; only a download walks every page, so a big network pays for the whole
 * list when it asked for the whole list and never just to render a table.
 */
const PAGE_SIZE = 100
const MAX_PAGES = 25
/** The most sites `getAll` and friends will return before giving up on further pages. */
export const SITE_LIST_LIMIT = PAGE_SIZE * MAX_PAGES

async function fetchSitePage(
  extra: Record<string, string>,
  page: number,
  pageSize: number = PAGE_SIZE
): Promise<{ sites: Site[]; totalCount?: number }> {
  const params = new URLSearchParams({
    concise: 'true',
    context: getContext(),
    includeActive: 'true',
    includeHidden: 'false',
    includeInactive: 'true',
    orderBy: 'name',
    orderByDirection: 'ASCENDING',
    ...extra,
    page: String(page),
    pageSize: String(pageSize),
  })
  const res = await apiFetch(`${API_BASE}/sites?${params}`, {
    headers: JSON_HEADERS,
  })
  if (!res.ok) await throwApiError(res, 'Failed to fetch sites')
  const data = (await res.json()) as { totalCount?: number }
  return { sites: toArray(data).map(mapSite), totalCount: data.totalCount }
}

/**
 * Every site the given parameters match. The first page usually reports the
 * total, in which case the pages after it are fetched at once; without a total
 * they are walked one at a time until a page comes back short.
 */
async function fetchAllSites(extra: Record<string, string>): Promise<Site[]> {
  const first = await fetchSitePage(extra, 1)
  if (first.sites.length < PAGE_SIZE) return first.sites

  if (first.totalCount !== undefined) {
    const pageCount = Math.min(Math.ceil(first.totalCount / PAGE_SIZE), MAX_PAGES)
    const rest = await Promise.all(
      Array.from({ length: pageCount - 1 }, (_, index) => fetchSitePage(extra, index + 2))
    )
    return [...first.sites, ...rest.flatMap((page) => page.sites)]
  }

  const all = [...first.sites]
  for (let page = 2; page <= MAX_PAGES; page++) {
    const next = await fetchSitePage(extra, page)
    all.push(...next.sites)
    if (next.sites.length < PAGE_SIZE) break
  }
  return all
}

/** A site's team links: the teams it is open to, and the one that owns it. */
export interface SiteTeams {
  id: string
  name: string
  ownerTeamId: string
  teamIds: string[]
}

function mapSiteTeams(r: Record<string, unknown>): SiteTeams {
  const ownerTeam = (r.ownerTeam ?? r.team) as Record<string, unknown> | undefined
  return {
    id: (r.id as string) ?? '',
    name: (r.name as string) ?? '',
    ownerTeamId: (ownerTeam?.id as string) ?? '',
    teamIds: ((r.teams as Array<Record<string, unknown> | string>) ?? []).map((team) =>
      typeof team === 'string' ? team : ((team.id as string) ?? '')
    ),
  }
}

/** Every page of sites matching `extra`, with their team links. */
function fetchAllSiteTeams(extra: Record<string, string>): Promise<SiteTeams[]> {
  return fetchAllPages(async (page, pageSize) => {
    const params = new URLSearchParams({
      context: getContext(),
      includeActive: 'true',
      includeHidden: 'false',
      includeInactive: 'true',
      orderBy: 'name',
      orderByDirection: 'ASCENDING',
      ...extra,
      page: String(page),
      pageSize: String(pageSize),
    })
    const res = await apiFetch(`${API_BASE}/sites?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) await throwApiError(res, 'Failed to fetch sites')
    const data = (await res.json()) as { totalCount?: number }
    return {
      results: toArray<Record<string, unknown>>(data).map(mapSiteTeams),
      totalCount: data.totalCount,
    }
  })
}

export interface SitePage {
  results: Site[]
  totalCount: number
  page: number
  pageSize: number
}

export interface SiteListOptions {
  page?: number
  pageSize?: number
  search?: string
  /** Country codes to narrow to; empty or missing means every country. */
  countries?: string[]
  /** Publisher (earnings account) id to narrow to; missing means every publisher. */
  earningsAccountId?: string
  /** Which object states to include. Defaults to both. */
  status?: ObjectStatus
  sort?: ListSort | null
}

/**
 * A `where` clause narrowing a site list to the given countries. The API's
 * `in` operator needs the spaces around it — written any other way the clause
 * is dropped silently and every site comes back.
 */
function countryWhere(countries: string[]): Record<string, string> {
  const codes = countries.filter(Boolean)
  return codes.length ? { where: `site.country in ${codes.join(',')}` } : {}
}

export const SitesApi = {
  /** The newest created, or most recently changed, active sites. */
  getRecent: async (sort: 'createTime' | 'updateTime', limit: number): Promise<Site[]> =>
    (
      await fetchSitePage(
        { orderBy: sort, orderByDirection: 'DESCENDING', includeInactive: 'false' },
        1,
        limit
      )
    ).sites,

  /** One page of sites, narrowed by the given filters on the server. */
  getPage: async ({
    page = 1,
    pageSize = 25,
    search,
    countries,
    earningsAccountId,
    status,
    sort,
  }: SiteListOptions = {}): Promise<SitePage> => {
    const { sites, totalCount } = await fetchSitePage(
      {
        ...searchParams('sites', search),
        ...countryWhere(countries ?? []),
        ...(earningsAccountId ? { earningsAccount: earningsAccountId } : {}),
        ...statusParams(status),
        ...sortParams(sort, {}),
      },
      page,
      pageSize
    )
    return { results: sites, totalCount: totalCount ?? sites.length, page, pageSize }
  },

  /**
   * Every site matching the filters, walked page by page. Only for downloads
   * and pickers — a list on screen should ask for the page it shows.
   */
  getAll: async (
    search?: string,
    countries?: string[],
    status?: ObjectStatus,
    earningsAccountId?: string
  ): Promise<Site[]> =>
    fetchAllSites({
      ...searchParams('sites', search),
      ...countryWhere(countries ?? []),
      ...(earningsAccountId ? { earningsAccount: earningsAccountId } : {}),
      ...statusParams(status),
    }),

  /** Every site in one of the given countries. */
  getByCountries: async (
    countries: string[],
    search?: string,
    status?: ObjectStatus
  ): Promise<Site[]> =>
    countries.length ? SitesApi.getAll(search, countries, status) : [],

  /** Every country the network has sites in, with how many sites each holds. */
  getCountries: async (): Promise<SiteCountry[]> => {
    const res = await apiFetch(`${API_BASE}/sites/availablecountries?context=${getContext()}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch site countries')
    const data = (await res.json()) as { countries?: SiteCountry[] }
    return (data.countries ?? []).filter((entry) => Boolean(entry.country))
  },

  getByEarningsAccount: async (
    earningsAccountId: string,
    search?: string,
    status?: ObjectStatus
  ): Promise<Site[]> =>
    fetchAllSites({
      ...searchParams('sites', search),
      earningsAccount: earningsAccountId,
      ...statusParams(status),
    }),

  /** Everything using the given blocklist or ruleset. */
  getByUsage: async (usageParam: UsageParam, resourceId: string): Promise<Site[]> => {
    const params = new URLSearchParams({
      concise: 'true',
      context: getContext(),
      [usageParam]: resourceId,
      includeActive: 'true',
      includeHidden: 'false',
      includeInactive: 'true',
      orderBy: 'name',
      orderByDirection: 'ASCENDING',
      page: '1',
      pageSize: '100',
    })
    const res = await apiFetch(`${API_BASE}/sites?${params}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch sites')
    const data = await res.json()
    const results = toArray(data)
    return results.map(mapSite)
  },

  /** Every site in a site group, which is how a group lists its members. */
  getBySiteGroup: async (siteGroupId: string): Promise<Site[]> =>
    fetchAllSites({ siteGroup: siteGroupId }),

  getById: async (id: string): Promise<Site> => {
    const res = await apiFetch(`${API_BASE}/sites/${id}?context=${getContext()}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch site')
    return mapSite(await res.json())
  },

  create: async (id: string, input: SiteInput): Promise<Site> => {
    const body = { id, ...toApiBody(input) }
    const res = await apiFetch(`${API_BASE}/sites/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(body),
    })
    if (!res.ok) await throwApiError(res, 'Failed to create site')
    return mapSite(await res.json())
  },

  update: async (id: string, input: SiteInput): Promise<Site> => {
    const res = await apiFetch(`${API_BASE}/sites/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(toApiBody(input)),
    })
    if (!res.ok) await throwApiError(res, 'Failed to save site')
    return mapSite(await res.json())
  },

  /** The sites a team is a member of, with which team owns each. */
  getTeamSites: (teamId: string): Promise<SiteTeams[]> => fetchAllSiteTeams({ team: teamId }),

  /**
   * Adds the team to some sites and takes it off others. A site's team list is
   * replaced as a whole on save, so each site is read first and sent back with
   * only this team added or removed.
   */
  setTeamMembership: async (teamId: string, add: string[], remove: string[]): Promise<void> => {
    const ids = [...add, ...remove]
    if (!ids.length) return
    const sites = await Promise.all(
      ids.map(async (id) => {
        const res = await apiFetch(`${API_BASE}/sites/${id}?context=${getContext()}`, {
          headers: JSON_HEADERS,
        })
        if (!res.ok) await throwApiError(res, 'Failed to fetch site')
        return mapSiteTeams(await res.json())
      })
    )
    const body = sites.map((site) =>
      add.includes(site.id)
        ? { id: site.id, teams: [...new Set([...site.teamIds, teamId])], removeTeams: [] }
        : {
            id: site.id,
            teams: site.teamIds.filter((id) => id !== teamId),
            removeTeams: [teamId],
          }
    )
    const res = await apiFetch(`${API_BASE}/sites?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(body),
    })
    if (!res.ok) await throwApiError(res, 'Failed to update site membership')
  },

  /** Makes a team the owner of sites, or clears their owner when `teamId` is null. */
  setOwnerTeam: async (siteIds: string[], teamId: string | null): Promise<void> => {
    if (!siteIds.length) return
    const body = siteIds.map((id) => ({ id, ownerTeam: teamId ? { id: teamId } : null }))
    const res = await apiFetch(`${API_BASE}/sites?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(body),
    })
    if (!res.ok) await throwApiError(res, 'Failed to update site ownership')
  },

  bulkSave: async (items: (SiteInput & { id: string })[]): Promise<Site[]> => {
    const body = items.map(({ id, ...input }) => ({ id, ...toApiBody(input) }))
    const res = await apiFetch(`${API_BASE}/sites?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(body),
    })
    if (!res.ok) await throwApiError(res, 'Failed to bulk save sites')
    const data = await res.json()
    const results = toArray(data)
    return results.map(mapSite)
  },
}
